"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Scroll-triggered fade-up wrapper. Adds `data-in="true"` when the element
 * enters the viewport; the visual transition is driven by CSS (`.reveal`
 * in globals.css) so it runs on the compositor.
 *
 * Use `delay` for stagger inside a list:
 *
 *   {items.map((x, i) => <Reveal key={x.id} delay={i * 80}>…</Reveal>)}
 *
 * Respects `prefers-reduced-motion` automatically — reduced-motion users
 * see the content instantly with no transform/opacity fade.
 */
export function Reveal({
  children,
  delay = 0,
  as: Tag = "div",
  className = "",
  once = true,
}: {
  children: React.ReactNode;
  delay?: number;
  /** Render as a different element (e.g. `"section"`). Default `"div"`. */
  as?: "div" | "section" | "li" | "span";
  className?: string;
  /** Stop observing after the first reveal. Default true. */
  once?: boolean;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Reduced-motion users: skip observer entirely and just render in place.
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setShown(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            if (once) observer.disconnect();
          } else if (!once) {
            setShown(false);
          }
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once]);

  const Component = Tag as React.ElementType;
  return (
    <Component
      ref={ref as React.RefObject<HTMLElement>}
      data-in={shown ? "true" : "false"}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      className={"reveal " + className}
    >
      {children}
    </Component>
  );
}
